import { Button } from "@/components/ui/button";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { motion } from "framer-motion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUIStore } from "@/stores/useUIStore";

interface Service {
    id: string;
    title: string;
    description: string;
    image: string;
    features: string[];
}

export const ServiceCard = ({ service, index }: { service: Service, index: number }) => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const isInView = useIntersectionObserver(ref, { threshold: 0.1 });
    const { openServiceModal } = useUIStore();

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: index * 0.1, duration: 0.6 }}
        className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col"
      >
        {/* Image with Parallax Effect */}
        <div className="relative w-full overflow-hidden aspect-[16/10]">
          <motion.img
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.4 }}
            src={service.image}
            alt={service.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
        </div>
        
        {/* Content */}
        <div className="p-6 flex flex-col flex-grow">
          <span className="text-sm font-medium text-brand-turquoise mb-2">{String(index + 1).padStart(2, "0")}</span>
          <h3 className="text-xl font-semibold mb-3 text-neutral-900">{service.title}</h3>
          <p className="text-neutral-600 mb-5 flex-grow leading-relaxed">{service.description}</p>
          <ul className="space-y-2 text-sm text-neutral-600">
            {service.features.slice(0, 3).map((feature, featureIndex) => (
              <li key={featureIndex} className="flex gap-2">
                <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-turquoise" aria-hidden />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Hover Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          className="absolute inset-0 bg-brand-turquoise/95 flex items-center justify-center p-4"
        >
          <Button
            className="bg-white text-brand-turquoise hover:bg-neutral-100"
            onClick={() => openServiceModal(service)}
          >
            {t('services.card_cta')}
          </Button>
        </motion.div>
      </motion.div>
    );
  };
